import { AuthorizedPersonDto } from 'src/app/core/data-models/authorized-person.data.models';
import { ClientInfoDto } from 'src/app/core/data-models/Company-info.data.models';
import { CompanyCityDto, NationalAddressDto } from 'src/app/core/data-models/national-address.data.models';
import { FirstStepData } from 'src/app/core/data-models/opportunity.data.models';

function hasFullNationalAddressShape(value: unknown): value is NationalAddressDto {
  if (value == null || typeof value !== 'object') return false;
  const o = value as Record<string, unknown>;
  return !!(
    o['buildingNumber'] ||
    o['street'] ||
    o['district'] ||
    o['postalCode'] ||
    o['additionalNumber'] ||
    o['unitNumber']
  );
}

function hasFullNationalAddressRaw(raw: Record<string, unknown>): boolean {
  return hasFullNationalAddressShape(raw);
}

function readField(raw: Record<string, unknown>, camel: string, pascal: string): unknown {
  return raw[camel] ?? raw[pascal];
}

/** Normalizes API/form address objects (camelCase or PascalCase) to portal DTO shape. */
export function normalizeNationalAddressFromApi(
  value: NationalAddressDto | Record<string, unknown> | null | undefined
): NationalAddressDto | null {
  if (value == null || typeof value !== 'object') return null;
  const raw = value as Record<string, unknown>;
  return mapNationalAddressForApi(raw);
}

/**
 * Resolves company address for fund step 1 when the UI uses full national-address fields.
 * Prefers a fat `companyNationalAddress` from the API, otherwise `nationalAddress` when the applicant has a separate address.
 */
export function hydrateCompanyAddressFromFirstStep(r: {
  companyNationalAddress?: CompanyCityDto | NationalAddressDto | null;
  nationalAddress?: NationalAddressDto | null;
  applicantNationalAddress?: NationalAddressDto | null;
}): NationalAddressDto | CompanyCityDto | null {
  const co = normalizeNationalAddressFromApi(r.companyNationalAddress as NationalAddressDto | null);
  if (co && hasFullNationalAddressShape(co)) {
    return co;
  }
  const legacy = normalizeNationalAddressFromApi(r.nationalAddress as NationalAddressDto | null);
  if (r.applicantNationalAddress && legacy && hasFullNationalAddressShape(legacy)) {
    return legacy;
  }
  if (co) return co;
  const cityOnly = r.companyNationalAddress;
  if (cityOnly && typeof cityOnly === 'object' && 'city' in cityOnly) {
    return cityOnly;
  }
  return null;
}

/** Same backend `NationalAddress` shape for applicant and company on fund step 1. */
export function hydrateApplicantAddressFromFirstStep(
  value: NationalAddressDto | Record<string, unknown> | null | undefined
): NationalAddressDto | null {
  return normalizeNationalAddressFromApi(value);
}

export interface Step1PayloadParts {
  clientInfoRaw: unknown;
  applicantIdentity: Partial<AuthorizedPersonDto>;
  compliance: Partial<AuthorizedPersonDto>;
  applicantAddressRaw: Record<string, unknown>;
  companyAddressRaw: Record<string, unknown>;
  fundId?: string;
  includeLegacyCompanyNationalAddress?: boolean;
}

export function mapNationalAddressForApi(raw: Record<string, unknown>): NationalAddressDto {
  const unit = readField(raw, 'unitNumber', 'UnitNumber');
  let unitNumber: number | null = null;
  if (unit !== '' && unit !== null && unit !== undefined) {
    const parsed = typeof unit === 'number' ? unit : parseInt(String(unit), 10);
    unitNumber = Number.isNaN(parsed) ? null : parsed;
  }

  return {
    buildingNumber: String(readField(raw, 'buildingNumber', 'BuildingNumber') ?? ''),
    street: String(readField(raw, 'street', 'Street') ?? ''),
    additionalNumber: String(readField(raw, 'additionalNumber', 'AdditionalNumber') ?? ''),
    district: String(readField(raw, 'district', 'District') ?? ''),
    city: String(readField(raw, 'city', 'City') ?? ''),
    postalCode: String(readField(raw, 'postalCode', 'PostalCode') ?? ''),
    unitNumber,
  };
}

export function buildFirstStepDataPayload(parts: Step1PayloadParts): FirstStepData {
  const companyNationalAddress: CompanyCityDto | NationalAddressDto =
    parts.includeLegacyCompanyNationalAddress && hasFullNationalAddressRaw(parts.companyAddressRaw)
      ? mapNationalAddressForApi(parts.companyAddressRaw)
      : { city: String(parts.companyAddressRaw['city'] ?? '') };

  const payload: FirstStepData = {
    clientInfo: parts.clientInfoRaw as ClientInfoDto,
    authorizedPerson: {
      ...parts.compliance,
      ...parts.applicantIdentity,
    } as AuthorizedPersonDto,
    applicantNationalAddress: mapNationalAddressForApi(parts.applicantAddressRaw),
    companyNationalAddress,
  };

  if (parts.fundId) {
    payload.fundId = parts.fundId;
  }

  

  return payload;
}
